import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowClockwise, Sparkle, PencilSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmDialog from './ConfirmDialog';
import { dmsApi, fmtDate, nfkc } from './dmsApi';

/**
 * L8/L9 — 読み直しと、読み取った内容の手直し。
 * これまで「もう一度読む」入口が無く、読み取った本文も直せなかった。
 * 読み直す前の内容は履歴に残るので、いつでも戻せる。
 */
export default function DocReadPanel({ documentId, canEdit, dark, onChanged }) {
  const [state, setState] = useState(null);
  const [fields, setFields] = useState(null);
  const [text, setText] = useState('');
  const [editText, setEditText] = useState(false);
  const [kw, setKw] = useState('');
  const [editKw, setEditKw] = useState(false);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [st, f] = await Promise.all([
        dmsApi.rereadState(documentId), dmsApi.readFields(documentId)]);
      setState(st); setFields(f);
      setText(f.ocr_text || '');
      setKw((f.ocr_keywords || []).join('、'));
      setVals(Object.fromEntries((f.fields || []).map((x) => [x.key, x.value || ''])));
    } catch { /* 権限や保護書類では開かない */ }
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  // 読み直し中だけ見張る（終わったら止める）
  useEffect(() => {
    if (state?.status !== 'processing') return undefined;
    timer.current = setTimeout(async () => {
      const st = await dmsApi.rereadState(documentId).catch(() => null);
      if (!st) return;
      setState(st);
      if (st.status !== 'processing') { load(); onChanged?.(); }
    }, 4000);
    return () => clearTimeout(timer.current);
  }, [state?.status, documentId, load, onChanged]);

  const start = async (mode, overwrite) => {
    setBusy(true);
    try {
      const r = await dmsApi.reread(documentId, mode, overwrite);
      toast.success(`${r.mode_label}を始めました — 終わると自動で表示されます`);
      setState((s) => ({ ...(s || {}), status: 'processing', mode, mode_label: r.mode_label }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === 'manual_edits_present') {
        setConfirm({
          title: '手で直した内容を上書きします',
          message: `${d.message_ja}\n読み直しても、前の内容は履歴から戻せます。`,
          confirmLabel: '上書きして読み直す', tone: 'danger',
          onConfirm: () => { setConfirm(null); start(mode, true); },
        });
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message_ja || '読み直しを始められませんでした'));
      }
    } finally { setBusy(false); }
  };

  const save = async (body, label) => {
    setBusy(true);
    try {
      await dmsApi.saveReadFields(documentId, body);
      toast.success(`${label}を保存しました（検索に反映しました）`);
      setEditText(false); setEditKw(false);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  const restore = (h) => setConfirm({
    title: '前の読み取り結果に戻す',
    message: `${fmtDate(h.at)}（${h.by}）の内容に戻します。\n今の内容も履歴に残ります。`,
    confirmLabel: '戻す',
    onConfirm: async () => {
      setConfirm(null); setBusy(true);
      try {
        await dmsApi.rereadRestore(documentId, h.id);
        toast.success('前の読み取り結果に戻しました');
        await load(); onChanged?.();
      } catch (e) { toast.error(e?.response?.data?.detail || '戻せませんでした'); }
      finally { setBusy(false); }
    },
  });

  if (!state || !fields) return null;
  const sub = dark ? 'text-zinc-400' : 'text-zinc-500';
  const box = dark ? 'bg-zinc-800/60 border-zinc-700 text-zinc-200'
    : 'bg-white border-zinc-200 text-zinc-800';

  return (
    <div className="space-y-3" data-testid="dms-read-panel">
      {/* ── 再読取 ───────────────────────────────────────────── */}
      <div>
        <p className={`text-[10px] font-bold ${sub} mb-1`}>もう一度読み取る</p>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Button size="sm" variant="outline" disabled={busy || state.status === 'processing' || !state.ocr_supported}
              onClick={() => start('ocr')} data-testid="dms-reread-ocr-btn"
              className="h-7 text-[10px] bg-transparent border-zinc-400/60">
              <ArrowClockwise size={12} className="mr-1" /> 文字だけ読み直す
            </Button>
            <Button size="sm" variant="outline" disabled={busy || state.status === 'processing'}
              onClick={() => start('ai')} data-testid="dms-reread-ai-btn"
              className="h-7 text-[10px] bg-transparent border-violet-500/60 text-violet-500">
              <Sparkle size={12} className="mr-1" /> AIで読み直す
            </Button>
          </div>
        ) : <p className={`text-[10px] ${sub}`}>読み直しには編集権限が必要です</p>}
        {!state.ocr_supported && (
          <p className="text-[10px] text-amber-500 mt-1" data-testid="dms-reread-unsupported">
            このファイル形式は文字の読み取りに対応していません（AIで読み直すのみ）
          </p>
        )}
        <p className={`text-[10px] mt-1 ${state.status === 'processing' ? 'text-amber-400'
          : state.status === 'failed' ? 'text-rose-400' : sub}`} data-testid="dms-reread-status">
          {state.status === 'processing' ? `${state.mode_label || ''}の最中です… この画面を離れても続きます`
            : state.status === 'failed' ? `前回の読み直しに失敗: ${state.error || ''}`
              : state.status === 'done' ? `最後の読み直し: ${state.mode_label || ''}（${fmtDate(state.finished_at)}）`
                : state.status === 'restored' ? '履歴から戻した状態です'
                  : '読み直しの記録はありません'}
        </p>
        {state.manual_edited_fields_ja?.length > 0 && (
          <p className="text-[10px] text-sky-400 mt-0.5" data-testid="dms-read-manual-note">
            手動修正: {state.manual_edited_fields_ja.join('・')}（読み直す前に確認します）
          </p>
        )}
        {state.history?.length > 0 && (
          <>
            <button type="button" onClick={() => setShowHistory((o) => !o)}
              className={`mt-1 inline-flex items-center gap-1 text-[10px] ${sub} hover:underline`}
              data-testid="dms-reread-history-toggle">
              <ClockCounterClockwise size={11} /> 読み取りの履歴 {state.history.length}件
            </button>
            {showHistory && (
              <ul className="mt-1 space-y-1" data-testid="dms-reread-history">
                {state.history.map((h) => (
                  <li key={h.id} className={`rounded border px-2 py-1 text-[10px] ${box}`}>
                    <span className="block">{fmtDate(h.at)} · {h.by}
                      {h.mode_label ? ` · ${h.mode_label}の前` : ''}</span>
                    <span className={sub}>本文 {h.text_length.toLocaleString()}文字 · キーワード {h.keyword_count}件</span>
                    {canEdit && (
                      <button type="button" onClick={() => restore(h)} disabled={busy}
                        data-testid={`dms-reread-restore-${h.id}`}
                        className="ml-1 text-teal-500 hover:underline">この内容に戻す</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {/* ── 読み取った本文 ───────────────────────────────────── */}
      <div>
        <p className={`text-[10px] font-bold ${sub} mb-1`}>読み取った本文</p>
        {editText ? (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
              data-testid="dms-read-text-area"
              className={`w-full rounded border p-2 text-[10px] font-mono ${box}`} />
            <div className="flex gap-1.5 mt-1">
              <Button size="sm" disabled={busy} onClick={() => save({ ocr_text: text }, '本文')}
                className="h-7 text-[10px] bg-teal-700 hover:bg-teal-800" data-testid="dms-read-text-save">保存</Button>
              <Button size="sm" variant="outline" onClick={() => { setEditText(false); setText(fields.ocr_text || ''); }}
                className="h-7 text-[10px] bg-transparent" data-testid="dms-read-text-cancel">キャンセル</Button>
            </div>
          </>
        ) : (
          <>
            <p className={`whitespace-pre-wrap max-h-32 overflow-y-auto rounded border p-2 text-[10px] ${box}`}
              data-testid="dms-read-text-view">{nfkc(fields.ocr_text) || '（まだ読み取っていません）'}</p>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setEditText(true)}
                className="h-7 text-[10px] mt-1 bg-transparent" data-testid="dms-read-text-edit-btn">
                <PencilSimple size={12} className="mr-1" /> 本文を編集
              </Button>
            )}
          </>
        )}
      </div>

      {/* ── 図番・改訂・責任者 ───────────────────────────────── */}
      <div>
        <p className={`text-[10px] font-bold ${sub} mb-1`}>読み取った項目</p>
        <div className="space-y-1">
          {(fields.fields || []).map((f) => (
            <div key={f.key} className="flex items-center gap-1.5">
              <span className={`w-14 shrink-0 text-[10px] ${sub}`}>{f.label_ja}</span>
              {canEdit ? (
                <Input value={vals[f.key] ?? ''} maxLength={80}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                  data-testid={`dms-read-field-${f.key}`}
                  className={`h-7 text-[10px] ${dark ? 'bg-zinc-800 border-zinc-600 text-zinc-100' : ''}`} />
              ) : <span className="text-[10px]">{nfkc(f.value) || '—'}</span>}
              {f.manual && <span className="text-[9px] text-sky-400 shrink-0">手動</span>}
            </div>
          ))}
        </div>
        {canEdit && (
          <Button size="sm" disabled={busy} data-testid="dms-read-fields-save"
            onClick={() => {
              const body = {};
              (fields.fields || []).forEach((f) => {
                if ((vals[f.key] ?? '') !== (f.value || '')) body[f.key] = vals[f.key] ?? '';
              });
              if (!Object.keys(body).length) { toast.info('変更がありません'); return; }
              save(body, '項目');
            }}
            className="h-7 text-[10px] mt-1.5 bg-teal-700 hover:bg-teal-800">項目を保存</Button>
        )}
      </div>

      {/* ── 検索キーワード ───────────────────────────────────── */}
      <div>
        <p className={`text-[10px] font-bold ${sub} mb-1`}>検索キーワード</p>
        {editKw ? (
          <>
            <textarea value={kw} onChange={(e) => setKw(e.target.value)} rows={4}
              data-testid="dms-read-keywords-area"
              className={`w-full rounded border p-2 text-[10px] ${box}`} />
            <p className={`text-[10px] ${sub} mt-0.5`}>読点（、）か カンマ で区切ってください</p>
            <div className="flex gap-1.5 mt-1">
              <Button size="sm" disabled={busy} data-testid="dms-read-keywords-save"
                onClick={() => save({ ocr_keywords: kw.split(/[、,\n]/).map((s) => s.trim()).filter(Boolean) }, 'キーワード')}
                className="h-7 text-[10px] bg-teal-700 hover:bg-teal-800">保存</Button>
              <Button size="sm" variant="outline" className="h-7 text-[10px] bg-transparent"
                onClick={() => { setEditKw(false); setKw((fields.ocr_keywords || []).join('、')); }}
                data-testid="dms-read-keywords-cancel">キャンセル</Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-1" data-testid="dms-read-keywords-view">
              {(fields.ocr_keywords || []).slice(0, 40).map((k) => (
                <span key={k} className={`text-[9px] rounded px-1.5 py-0.5 ${
                  dark ? 'bg-zinc-700/70 text-zinc-200' : 'bg-zinc-100 text-zinc-700'}`}>{nfkc(k)}</span>
              ))}
              {(fields.ocr_keywords || []).length === 0 && <span className={`text-[10px] ${sub}`}>未生成</span>}
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setEditKw(true)}
                className="h-7 text-[10px] mt-1 bg-transparent" data-testid="dms-read-keywords-edit-btn">
                <PencilSimple size={12} className="mr-1" /> キーワードを編集
              </Button>
            )}
          </>
        )}
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        confirmLabel={confirm?.confirmLabel} tone={confirm?.tone} testid="dms-read-confirm"
        onCancel={() => setConfirm(null)} onConfirm={confirm?.onConfirm} />
    </div>
  );
}
